import styled, { css } from 'styled-components'
import { useDraggable } from '@dnd-kit/core'

const Card = styled.div`
    background: ${({ theme, $selected }) => $selected ? `${theme.btn.primary}12` : theme.card.background};
    border: 1.5px solid ${({ theme, $selected }) => $selected ? theme.btn.primary : theme.area.lightBorder};
    border-left: ${({ theme, $selected }) => $selected ? `3px solid ${theme.btn.primary}` : `1.5px solid ${theme.area.lightBorder}`};
    border-radius: 0.5rem;
    padding: 0.6rem 0.75rem;
    cursor: grab;
    user-select: none;
    min-width: 160px;
    max-width: 210px;
    flex-shrink: 0;
    transition: box-shadow 0.15s, border-color 0.15s, background 0.15s, opacity 0.15s;
    opacity: ${({ $dragging }) => $dragging ? 0.4 : 1};

    ${({ $selected, theme }) => $selected && css`
        box-shadow: 0 0 0 2px ${theme.btn.primary}33;
    `}

    &:hover {
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    &:active {
        cursor: grabbing;
    }
`

const PlacementRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.3rem;
    margin-top: 0.25rem;
`

const Placement = styled.div`
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.btn.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
    flex: 1;
    min-width: 0;
`

const UnscheduleBtn = styled.button`
    flex-shrink: 0;
    background: none;
    border: none;
    cursor: pointer;
    padding: 1px 3px;
    border-radius: 3px;
    font-size: 0.75rem;
    line-height: 1;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
    transition: color 0.15s, background 0.15s;

    &:hover {
        color: ${({ theme }) => theme.btn.danger};
        background: ${({ theme }) => theme.area.highBg};
    }
`

const Seq = styled.span`
    font-size: var(--font-XS);
    font-weight: 700;
    color: ${({ theme }) => theme.font.label};
    margin-right: 0.4rem;
`

const Name = styled.div`
    font-size: var(--font-S);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const Meta = styled.div`
    margin-top: 0.35rem;
    font-size: var(--font-XS);
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const fmtTime = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
}

const WorkPlannerOperationCard = ({ op, selected, onSelect, onUnschedule, fieldConfig }) => {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: op.id,
        data: { op }
    })

    const seqField    = fieldConfig?.sequence     ?? 'sequence'
    const labelField  = fieldConfig?.taskLabel    ?? 'name'
    const capField    = fieldConfig?.capabilityName ?? 'workCapabilityName'
    const totalField  = fieldConfig?.totalTime    ?? 'totalTime'
    const startField  = fieldConfig?.startTime    ?? 'plannedStart'
    const endField    = fieldConfig?.endTime      ?? 'plannedEnd'
    const resIdField  = fieldConfig?.resourceId   ?? 'workCenterId'
    const resNameField = fieldConfig?.resourceName ?? 'workCenterName'

    const seqValue   = op[seqField]
    const labelValue = op[labelField] || op.number || op.description || '—'

    const isScheduled = !!(op[startField] && op[resIdField])
    const placementLabel = isScheduled
        ? `${op[resNameField] ?? ''}  ${fmtTime(op[startField])} – ${fmtTime(op[endField])}`
        : null

    return (
        <Card
            ref={setNodeRef}
            $selected={selected}
            $dragging={isDragging}
            onClick={() => onSelect(op.id)}
            {...listeners}
            {...attributes}
        >
            <Name>
                {seqValue != null && <Seq>{seqValue}.</Seq>}
                {labelValue}
            </Name>
            <Meta>
                {op[capField] && <span>{op[capField]}</span>}
                {op[totalField] != null && <span style={{ marginLeft: '0.5rem' }}>{op[totalField]} min</span>}
            </Meta>
            {placementLabel && (
                <PlacementRow>
                    <Placement>{placementLabel}</Placement>
                    {onUnschedule && (
                        <UnscheduleBtn
                            title="Remove from schedule"
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onUnschedule(op) }}
                        >
                            ✕
                        </UnscheduleBtn>
                    )}
                </PlacementRow>
            )}
        </Card>
    )
}

export default WorkPlannerOperationCard
